import { booleanAttribute, computed, Directive, input } from '@angular/core';

/**
 * Turns a native `<button>` or `<a>` into a hit area around whatever it holds, and draws nothing
 * itself.
 *
 * ```html
 * <button mibBareButton [mibMenuTrigger]="account" aria-label="Account">
 *   <mib-avatar username="Dr. Martin" size="sm" mode="subtle" />
 * </button>
 * ```
 *
 * For a control whose face is already drawn: an avatar that opens a menu, a thumbnail that opens a
 * preview, a logo that goes home. Where `[mibButton]` gives a height off the control scale, an inset,
 * a theme and a mode, this gives none of them.
 *
 * It adds the pointer and the focus ring of the system, and hugs its content, so the hit area is
 * exactly the drawing and never larger — unless `fill` makes it cover the box the page gives it.
 *
 * It never takes a class of its own: everything visible belongs to the child, and the button reads
 * what it needs from it. It hugs the child's box, sits on the system corner, and turns round by
 * itself when the direct child carries `rounded-full` — a bare `<mib-avatar>` declares it on its own
 * box — so the focus ring hugs the shape either way.
 */
@Directive({
  selector: 'button[mibBareButton], a[mibBareButton]',
  host: {
    '[class]': 'classes()',
  },
})
export class MibBareButtonDirective {
  /**
   * Covers the box it is given instead of hugging its content, its child stretched to that box; the
   * focus ring is drawn inside, so an `overflow-hidden` parent never clips it.
   */
  readonly fill = input(false, { transform: booleanAttribute });

  /** The classes the host carries; internal, never written by hand. */
  readonly classes = computed(() =>
    [
      this.fill()
        ? 'grid size-full -outline-offset-2'
        : 'inline-flex w-fit items-center justify-center outline-offset-2',
      'cursor-pointer bg-transparent p-0 rounded-mib',
      'has-[>.rounded-full]:rounded-full',
      'focus-visible:outline-2 outline-mib-primary',
      'disabled:cursor-default disabled:opacity-40',
    ].join(' '),
  );
}
